import { AdEvent, AdTrackingEvent } from '@quickplay/player-interface';
import { EventEmitter } from '@quickplay/foundation';

/**
 * Quartile threshold configuration
 */
interface QuartileThreshold {
  thresholdPercent: number;
  trackingEvent: AdTrackingEvent;
}

/**
 * Tracks and emits ad quartile events (25%, 50%, 75%) during ad playback.
 * Ensures each quartile event is only fired once per ad.
 */
export class AdQuartileTracker {
  private readonly firedQuartiles = new Set<number>();
  private readonly eventEmitter: EventEmitter;

  /**
   * Quartile thresholds lazily initialized to avoid accessing AdTrackingEvent
   * at module load time, which can cause issues with external module load order.
   */
  private static getQuartileThresholds(): QuartileThreshold[] {
    return [
      { thresholdPercent: 25, trackingEvent: AdTrackingEvent.AD_FIRST_QUARTILE_EVENT },
      { thresholdPercent: 50, trackingEvent: AdTrackingEvent.AD_MIDPOINT_EVENT },
      { thresholdPercent: 75, trackingEvent: AdTrackingEvent.AD_THIRD_QUARTILE_EVENT },
    ];
  }

  constructor(eventEmitter: EventEmitter) {
    this.eventEmitter = eventEmitter;
  }

  /**
   * Resets the tracker for a new ad. Should be called when an ad starts.
   */
  reset(): void {
    this.firedQuartiles.clear();
  }

  /**
   * Checks ad progress and emits quartile events if thresholds are crossed.
   * @param progress - Current playback progress in seconds
   * @param adDuration - Total ad duration in seconds
   */
  emitQuartileEvents(progress: number, adDuration: number): void {
    if (adDuration <= 0) return;

    const percent = (progress / adDuration) * 100;

    for (const { thresholdPercent, trackingEvent } of AdQuartileTracker.getQuartileThresholds()) {
      if (percent >= thresholdPercent && !this.firedQuartiles.has(thresholdPercent)) {
        this.firedQuartiles.add(thresholdPercent);
        this.eventEmitter.publish(AdEvent.ADVERT_TRACKING_EVENT, trackingEvent);
      }
    }
  }
}
